// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { strapiAuthService } from '../services/api/strapiAuth';

// Créer le Context
const AuthContext = createContext(null);

// Provider qui entoure l'application
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Au démarrage de l'application, vérifier si un utilisateur est déjà connecté
    useEffect(() => {
        const initAuth = async () => {
            try {
                const token = localStorage.getItem('strapiToken');
                const storedUser = localStorage.getItem('strapiUser');

                if (token && storedUser) {
                    // Vérifier que le token est toujours valide
                    try {
                        const profile = await strapiAuthService.getProfile();
                        setUser(profile);
                        setIsAuthenticated(true);
                    } catch (error) {
                        // Token expiré ou invalide, nettoyer
                        console.error('Token invalide lors de l\'initialisation:', error);
                        strapiAuthService.logout();
                        setUser(null);
                        setIsAuthenticated(false);
                    }
                }
            } catch (error) {
                console.error('Erreur lors de l\'initialisation de l\'auth:', error);
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();
    }, []);

    // Fonction de connexion
    const login = async (identifier, password) => {
        try {
            await strapiAuthService.login(identifier, password);

            // Récupérer le profil complet avec les formations (trainings)
            const profile = await strapiAuthService.getProfile();
            setUser(profile);
            setIsAuthenticated(true);

            await new Promise(resolve => setTimeout(resolve, 100));

            return { success: true };
        } catch (error) {
            throw error;
        }
    };

    // Fonction de déconnexion
    const logout = () => {
        strapiAuthService.logout();
        setUser(null);
        setIsAuthenticated(false);
    };

    // Fonction pour mettre à jour les infos utilisateur (après modification profil)
    const updateUser = async () => {
        try {
            const profile = await strapiAuthService.getProfile();
            setUser(profile);
        } catch (error) {
            console.error('Erreur lors de la mise à jour du profil:', error);
        }
    };


    // Valeurs et fonctions exposées à toute l'application
    const value = {
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook personnalisé pour utiliser le Context facilement
export const useAuth = () => {
    const context = useContext(AuthContext);
    
    if (!context) {
        throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
    }
    
    return context;
};